import React, { useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import Button from '../../../../../../components/button';
import ServerError from '../../../../../../components/ServerError';
import { useBlockedFeature } from '../../../../../../hooks/useServerFeature';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { DataWithLoadingState } from '../../../../../../utils/dataWithLoadingState';
import { SkeletonTable } from '../../../../components/Skeleton/Table';
import { useProjectSkillsQuery } from '../../../../__generated__/graphql';
import { EmptyState } from '../../../../components/EmptyStateCard';
import { useUserInfo } from '../../../../context/UserInfo';
import { EmployeeSkillsTable } from '../../../Skills/common/EmployeeSkillsTable';
import { Skill, SkillLevel } from '../../../Skills/common/types';
import { AddProjectSkillDialogConnected } from './AddProjectSkillDialogConnected';
import {
  mapAvailableProjectSkills,
  mapProjectSkillAssociation,
} from './mappings';
import { RemoveProjectSkillDialogConnected } from './RemoveProjectSkillDialogConnected';
import {
  useUpdateProjectSkillRate,
  useUpdateProjectSkillTop,
} from './useProjectSkillsMutations';

type Props = {
  projectId: string;
  projectName: string;
  isReadonly: boolean;
};

export const ProjectSkillsSection: React.FC<Props> = ({
  projectId,
  projectName,
  isReadonly,
}) => {
  const t = useNamespacedTranslation('skills.projectSkills');
  const tRemoveDialog = useNamespacedTranslation('skills.removeProjectSkill');
  const { isManagerModeAvailable } = useUserInfo();
  const isSkillAssignmentBlocked = useBlockedFeature('Skill_Assignment');
  const effectiveReadonly =
    isReadonly || !isManagerModeAvailable || isSkillAssignmentBlocked;

  const { data, loading, error } = useProjectSkillsQuery({
    variables: { projectId },
  });

  const { updateProjectSkillTop } = useUpdateProjectSkillTop();
  const { updateProjectSkillRate } = useUpdateProjectSkillRate();

  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [skillToRemove, setSkillToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const projectSkills = useMemo(
    () =>
      data?.project
        ? data.project.projectSkills.map(mapProjectSkillAssociation)
        : null,
    [data],
  );

  const availableSkills: DataWithLoadingState<Skill> = useMemo(() => {
    if (error) return 'error';
    if (!data || loading) return 'loading';
    if (!data.project) return [];
    return mapAvailableProjectSkills(data.skills, data.project.projectSkills);
  }, [data, loading, error]);

  const handleAddSkillClick = useCallback(() => setAddDialogVisible(true), []);
  const handleAddSkillDialogClose = useCallback(
    () => setAddDialogVisible(false),
    [],
  );

  const handleRemoveSkill = useCallback(
    (id: string) => {
      const skill = projectSkills?.find((x) => x.id === id);
      if (skill) {
        setSkillToRemove({ id, name: skill.name });
      }
    },
    [projectSkills],
  );
  const handleRemoveDialogClose = useCallback(() => setSkillToRemove(null), []);

  const handleChangeSkillLevel = useCallback(
    (id: string, level: SkillLevel) => {
      updateProjectSkillRate({ id, projectId, rate: level });
    },
    [updateProjectSkillRate, projectId],
  );

  const handleChooseSkill = useCallback(
    (id: string, isChoosen: boolean) => {
      updateProjectSkillTop({ id, projectId, top: isChoosen });
    },
    [updateProjectSkillTop, projectId],
  );

  if (error || (!loading && !data?.project)) {
    return <ServerError />;
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4 className="card-header-title">{t('header')}</h4>
        {!effectiveReadonly && (
          <Button
            label={t('assignSkill')}
            size="sm"
            onClick={handleAddSkillClick}
          />
        )}
      </div>
      <div className="card-body p-0">
        {loading || !projectSkills ? (
          <Loading />
        ) : projectSkills.length === 0 ? (
          <div className="d-flex small flex-column align-items-center p-3">
            <EmptyState title={t('empty')} />
          </div>
        ) : (
          <EmployeeSkillsTable
            employeeSkills={projectSkills}
            onChooseSkill={handleChooseSkill}
            onChangeSkillLevel={handleChangeSkillLevel}
            onRemoveSkill={handleRemoveSkill}
            isReadonly={effectiveReadonly}
          />
        )}
      </div>
      <AddProjectSkillDialogConnected
        projectId={projectId}
        availableSkills={availableSkills}
        show={addDialogVisible}
        onClose={handleAddSkillDialogClose}
      />
      {!effectiveReadonly && (
        <RemoveProjectSkillDialogConnected
          onClose={handleRemoveDialogClose}
          skillIdToRemove={skillToRemove?.id ?? null}
          projectId={projectId}
          removeSkillText={tRemoveDialog('removeSkillText', {
            skill: skillToRemove?.name,
            project: projectName,
          })}
        />
      )}
    </div>
  );
};

const Loading: React.FC = () => (
  <div>
    <Skeleton height={18} width="50%" className="mx-4 mb-3" />
    <SkeletonTable columns={3} rows={3} />
  </div>
);
